/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import {
	DefaultSplitDataRenderType,
	generateSimplestList,
	Plan,
	SimpleAttributeList,
	SplitData,
} from '@touchplan/common';

/**
 * We connect our SplitData model to the Fbdb/Firebase Active Line data.
 * The ActiveLineService will then dispatch a client-side `IActiveLine` model that observers can consume.
 */
export interface IActiveLine {
	activeFinishX?: number;
	activeFinishDate?: string;
	activeLineDate?: string;
	activeLineX?: number;
	activeStartDate?: string;
	activeStartX?: number;
	hasActiveSide?: boolean;
}
/**
 * ActiveLine
 * The ActiveLine model represents the boundary between active and pull space
 * Where the "Active space" is to the left of the active line and "pull space" is to the right
 */
export class ActiveLine extends SplitData<IActiveLine, DefaultSplitDataRenderType> {
	protected attributeList: SimpleAttributeList = generateSimplestList([
		'activeFinishX',
		'activeFinishDate',
		'activeLineDate',
		'activeLineX',
		'activeStartDate',
		'activeStartX',
		`hasActiveSide`,
	]);

	constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}
	/**
	 * Apply an update from the database
	 * The input data for this method is a `Plan` object because the active line data lives flat
	 * on the Plan object in Firebase (Root/plans/<projectId>/<planId>), so in order to watch for changes we will need to
	 * either subscribe to each property individually, or watch the Plan object and then build a model
	 * @param data Current state of the Plan object.
	 */
	public databaseUpdate(data: IActiveLine): void {
		let activeLine: IActiveLine = {} as IActiveLine;
		for (const key in data) {
			if (this.attributeList[key]) {
				activeLine[key] = data[key];
			}
		}
		super.databaseUpdate(activeLine);
		this.recalcLocal(DefaultSplitDataRenderType.database);
	}

	/**
	 * Applies a local (current view) update to the data
	 * @param data
	 * @param renderType
	 */
	public localUpdate(data: object, renderType?: DefaultSplitDataRenderType): void {
		super.localUpdate(data, renderType);
		this.recalcLocal(renderType);
	}
	/**
	 *
	 * @param renderType
	 * @private
	 */
	private recalcLocal(renderType?: DefaultSplitDataRenderType): void {
		// BEING PLACEHOLDER: Replace as part of TP-6873
		if (!renderType) {
			renderType = this.currentRenderType;
		}
		const view = this[renderType];
		// END PLACEHOLDER
		view.hasActiveSide = view.activeLineX !== undefined;
	}
}
